import { useEffect, useRef, useState } from 'react';
import { createMessageQueue, runMessageQueue } from '../utils/chatMessageQueue';

// Keep interrupted queues per chat while the panel is mounted. A late response
// must never clear another chat's composer or insert a message into its feed.
export function useChatMessageQueue({ sessionKey, send, onMessage, onDone, enabled = true, isCurrent = () => true }) {
  const jobs = useRef(new Map());
  const active = useRef(sessionKey);
  active.current = enabled ? sessionKey : null;
  const mounted = useRef(true);
  const [, redraw] = useState(0);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const changed = () => { if (mounted.current) redraw(n => n + 1); };
  const job = jobs.current.get(sessionKey) || null;

  const submit = async payload => {
    const key = sessionKey;
    let current = jobs.current.get(key);
    if (!current) {
      if (!payload) return false;
      current = createMessageQueue(payload);
      jobs.current.set(key, current);
    }
    if (current.running) return false;
    const isActive = () => mounted.current && active.current === key && isCurrent();
    const done = await runMessageQueue(current, {
      send, isActive, onChange: changed,
      onMessage: message => { if (isActive()) onMessage(message); },
    });
    if (done) {
      jobs.current.delete(key);
      if (isActive()) onDone();
      changed();
    }
    return done;
  };

  const discard = () => {
    const current = jobs.current.get(sessionKey);
    if (current?.running) return;
    jobs.current.delete(sessionKey);
    onDone();
    changed();
  };

  return { job, submit, discard };
}
